<template>
  <section v-if="!loading">
    <JoinOrCreateTeam
      v-if="!joinedTeam"
      :code="code"
      :teams="teams"
      :eventName="eventName"
      @refreshTeamList="refreshTeamList"
      @submitNewTeamName="submitNewTeamName"
      @submitSelectTeamAndAddMember="submitSelectTeamAndAddMember"
    />
    <GameOnboardingTips
      v-else
      :eventName="eventName"
      :code="code"
      :team_id="teamId"
      @finished="proceedToGame"
    />
  </section>
</template>

<script>
import JoinOrCreateTeam from '@/components/JoinOrCreateTeam.vue'
import GameOnboardingTips from '@/components/GameOnboardingTips.vue'
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import { spookyNavMixin } from '../mixins/spookyNavMixin'
import { challengeNavigationMixin } from '../mixins/challengeNavigationMixin'

export default {
  mixins: [spookyNavMixin, challengeNavigationMixin],
  components: { JoinOrCreateTeam, GameOnboardingTips },
  props: ['code'],
  created() {
    if (this.game?.game === undefined || this.game?.game?.id != this.code) {
      this.fetchGame({ code: this.code })
    }
    if (
      this.game.teams === undefined ||
      this.game.teams[0]?.gameCode != this.code
    ) {
      this.fetchTeams({ code: this.code })
    }
  },
  mounted() {
    this.removeGameClassFromBody()
    document.body.classList.add('onboarding')

    // If the page is mounted and the data is already
    // loasded then the watcher below won't fire for
    // question index changed so we need to fire it manually

    if (this.teamId !== undefined && this.joinedTeam) {
      if (this.game.teamData?.gameData === undefined) {
        this.fetchTeamData({ code: this.code, teamId: this.teamId })
      } else {
        console.log(
          `🦄 mounted and got game data (current question index: ${this.currentChallengeData?.currentQuestionIndex})`
        )

        if (this.proposedChallengeIndex !== undefined) {
          this.handleProposedChallengeIndexChanged(
            this.proposedChallengeIndex,
            undefined
          )
        } else if (
          this.currentChallengeIndex !== null ||
          this.currentChallengeIndex !== undefined
        ) {
          this.handleChallengeIndexChanged(
            this.currentChallengeIndex,
            undefined
          )
        }
      }
    } else {
      this.unbindTeamData()
    }
  },
  watch: {
    proposedChallengeIndex(newValue, oldValue) {
      this.handleProposedChallengeIndexChanged(newValue, oldValue)
    },
    currentChallengeIndex(newValue, oldValue) {
      this.handleChallengeIndexChanged(newValue, oldValue)
    }
  },
  data: () => ({
    teamName: '',
    teamMemberName: null,
    teamId: undefined,
    addedToExistingTeam: false,
    snackbar: false,
    snackbarText: 'Unexpected error',
    snackbarTimeout: 5000,
    joinedTeam: false
  }),
  methods: {
    ...mapActions('game', [
      'fetchTeams',
      'fetchTeamData',
      'fetchGame',
      'unbindTeamData'
    ]),
    moment: function(date) {
      return moment(date)
    },
    removeGameClassFromBody() {
      console.log(`removeGameClass`)
      document.body.classList.remove('great-games-challenge')
      document.body.classList.remove('the-perfect-pitch')
      document.body.classList.remove('big-escape')
      document.body.classList.remove('save-pop-culture')
      document.body.classList.remove('the-big-quiz')
    },
    refreshTeamList() {
      this.fetchTeams({ code: this.code })
    },
    submitNewTeamName(props) {
      this.$store
        .dispatch('game/createGame', {
          code: this.code,
          teamName: props.teamName
        })
        .then(() => {
          props.success()
        })
        .catch(error => {
          props.error(error)
        })
    },
    submitSelectTeamAndAddMember(teamId, teamName, fullName) {
      this.teamId = teamId
      this.teamName = teamName
      this.addMemberToTeam(fullName)
    },
    addMemberToTeam(fullName) {
      //TODO: Show Joining team... animation
      this.$store
        .dispatch('game/addMemberToTeam', {
          code: this.code,
          teamId: this.teamId,
          teamMemberName: fullName
        })
        .then(() => {
          console.log('added team member to team')
          this.snackbarText = 'Team updated'
          this.snackbar = true

          this.teamMemberName = fullName
          this.addedToExistingTeam = true

          this.joinedTeam = true

          this.fetchTeamData({ code: this.code, teamId: this.teamId })
        })
        .catch(error => {
          //TODO: Display error
          console.log('Error adding team member to team: ', error)
          this.snackbarText = error
          this.snackbar = true
        })
    },
    handleProposedChallengeIndexChanged(newValue, oldValue) {
      console.log(
        `🦊🦊🦊 GameTeamCreated: proposedChallengeIndex changing from ${oldValue} to ${newValue}`
      )

      if (newValue === undefined && oldValue !== undefined) {
        // This means that the modal is currently displayed and the user
        // has responded, so now we need to hide it

        this.$modal.hide('propose-challenge-change-overlay')
        return
      }

      let message, submessage
      if (newValue === this.$constants.GAMEBOARD_CHALLENGE_INDEX) {
        if (this.gameData?.proposerName === undefined) {
          message = 'A player chose to go to the Challenge List'
        } else {
          message = `${this.gameData?.proposerName} chose to go to the Challenge List`
        }
      } else if (newValue === this.$constants.INTRO_CHALLENGE_INDEX) {
        message = `Your devices are now synchronised!`
        if (this.gameData?.proposerName === undefined) {
          submessage = `A player chose to start the game`
        } else {
          submessage = `${this.gameData?.proposerName} chose to start the game`
        }
      } else {
        if (this.gameData?.proposerName === undefined) {
          message = `A player chose the ${this.gameData?.challenges[newValue].title} challenge`
        } else {
          message = `${this.gameData?.proposerName} chose the ${this.gameData?.challenges[newValue].title} challenge`
        }
      }

      this.$modal.show('propose-challenge-change-overlay', {
        proposedChallengeIndex: newValue,
        code: this.code,
        team_id: this.teamId,
        submessage: submessage,
        message: message
      })
    },
    handleChallengeIndexChanged(newValue, oldValue) {
      console.log(
        `🦹‍♀️🦹‍♀️🦹‍♀️ GameTeamCreate: currentChallengeIndex changing from ${oldValue} to ${newValue}`
      )

      if (this.teamId === undefined) {
        console.log(
          `🦹‍♀️🦹‍♀️🦹‍♀️ GameTeamCreate: currentChallengeIndex changed but teamId not set yet.`
        )
        return
      }

      if (newValue === null || newValue === undefined) {
        return
      }

      if (newValue === this.$constants.GAMEBOARD_CHALLENGE_INDEX) {
        console.log('🧙‍♀️Return to Challenge List')

        this.doSpookyNav(
          'challenge-play',
          {
            code: this.code,
            team_id: this.teamId,
            challengeIndex: this.$constants.GAMEBOARD_CHALLENGE_INDEX
          },
          'Loading',
          'Game Board'
        )
      } else if (newValue === this.$constants.INTRO_CHALLENGE_INDEX) {
        console.log(`🧟‍♂️Jump to intro`)

        this.doSpookyNav(
          'challenge-play',
          {
            code: this.code,
            team_id: this.teamId,
            challengeIndex: this.$constants.INTRO_CHALLENGE_INDEX
          },
          'Loading',
          'Introduction'
        )
      } else {
        console.log(`🧟‍♂️Jump to another challenge: ${newValue}`)

        this.doSpookyNav(
          'challenge-play',
          {
            code: this.code,
            team_id: this.teamId,
            challengeIndex: newValue
          },
          'Loading Challenge',
          `${this.gameData?.challenges[newValue].title}`
        )
      }
    },
    proceedToGame() {
      this.setProposedChallengeIndex(
        this.code,
        this.teamId,
        this.$constants.INTRO_CHALLENGE_INDEX
      )
    }
  },
  computed: {
    ...mapState({
      game: state => state.game
    }),
    eventName() {
      if (this.game.game?.eventName) {
        return this.game.game?.eventName
      }

      return this.game.game?.gameData?.title
    },
    gameData() {
      return this.game.teamData?.gameData
    },
    teams() {
      return this.game?.teams
    },
    loading() {
      return this.game.game?.gameData === undefined
    },
    proposedChallengeIndex() {
      return this.gameData?.proposedChallengeIndex
    },
    currentChallengeIndex() {
      return this.gameData?.currentChallengeIndex
    }
  }
}
</script>
